/* eslint-disable @typescript-eslint/no-explicit-any */

import { refreshAccessToken } from './refreshAccessToken';
import { getSessionEpoch } from './session';
import { getBaseApi } from './runtimeOrg';

interface ApiClientOptions extends RequestInit {
  headers?: HeadersInit;
  body?: any;
}

function isAbsoluteHttpUrl(u?: string) {
  return !!u && /^https?:\/\//i.test(u);
}

function joinUrl(base: string, path: string): string {
  if (!base) return path;
  if (isAbsoluteHttpUrl(path)) return path;
  const b = base.replace(/\/+$/, '');
  const p = String(path || '').replace(/^\/+/, '');
  return `${b}/${p}`;
}

function pickMsg(data: any, fallback = 'API request failed'): string {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (Array.isArray(data?.error)) return data.error.join(' ');
  if (data?.error) return String(data.error);
  if (data?.detail) return String(data.detail);
  if (data?.message) return String(data.message);
  return fallback;
}

function isFormDataBody(body: any): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

function isFileOrBlob(body: any): body is File | Blob {
  return (
    (typeof File !== 'undefined' && body instanceof File) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  );
}

export const apiClient = async (
  endpoint: string,
  { method = 'GET', headers = {}, body, ...customConfig }: ApiClientOptions = {}
) => {
  const baseApi = getBaseApi();
  const url = joinUrl(baseApi, endpoint);
  const startEpoch = getSessionEpoch();

  const readAccessToken = () =>
    (typeof window !== 'undefined'
      ? localStorage.getItem('accessToken')
      : undefined) ?? undefined;

  const buildConfig = (token?: string): RequestInit => {
    const h = new Headers(headers);
    if (!h.has('Accept')) h.set('Accept', 'application/json');

    let finalBody: any = body;

    if (isFormDataBody(finalBody)) {
      h.delete('Content-Type');
    } else if (isFileOrBlob(finalBody)) {
      const fd = new FormData();
      fd.append('file', finalBody);
      finalBody = fd;
      h.delete('Content-Type');
    } else if (
      finalBody &&
      typeof finalBody === 'object' &&
      typeof finalBody !== 'string'
    ) {
      if (!h.has('Content-Type')) h.set('Content-Type', 'application/json');
      finalBody = JSON.stringify(finalBody);
    } else if (typeof finalBody === 'string') {
      if (!h.has('Content-Type'))
        h.set('Content-Type', 'text/plain;charset=UTF-8');
    }

    if (token) h.set('Authorization', `Bearer ${token}`);

    const init: RequestInit = {
      method: method.toUpperCase(),
      headers: h,
      ...customConfig,
    };

    if (
      method.toUpperCase() !== 'GET' &&
      method.toUpperCase() !== 'HEAD' &&
      finalBody != null
    ) {
      (init as any).body = finalBody;
    }

    return init;
  };

  const doFetch = (token?: string) => fetch(url, buildConfig(token));

  let token = readAccessToken();
  let response = await doFetch(token);

  if (response.status === 401) {
    const snapshot = await response
      .clone()
      .json()
      .catch(() => null as any);
    const tokenLikelyInvalid =
      snapshot?.code === 'token_not_valid' ||
      snapshot?.detail === 'Given token not valid for any token type';

    if (tokenLikelyInvalid) {
      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          if (typeof window !== 'undefined') {
            localStorage.setItem('accessToken', String(newToken));
          }
          token = String(newToken);
          response = await doFetch(token);
        } else {
          throw new Error('no_token');
        }
      } catch {
        if (startEpoch === getSessionEpoch()) {
          try {
            const [{ store }, modPerform] = await Promise.all([
              import('@/redux/store'),
              import('@/redux/auth/signin/performSignOut'),
            ]);
            const performSignOut = (modPerform as any).performSignOut;
            if (performSignOut && (store as any)?.dispatch) {
              await performSignOut()((store as any).dispatch);
            } else if (typeof window !== 'undefined') {
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('username');
              localStorage.removeItem('userProfile');
            }
          } catch {
            try {
              if (typeof window !== 'undefined') {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
              }
            } catch {}
          }
        }
        response = await doFetch(undefined);
      }
    }
  }

  const contentType = response.headers.get('Content-Type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const e: any = new Error(pickMsg(data, `HTTP ${response.status}`));
    e.status = response.status;
    e.response = data;
    throw e;
  }

  return data;
};
